import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import SnSheet from "@shared/components/Grid/SnSheet";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import { menuInfo } from "@shared/type/CommonType";
import {
  getInitForm,
  getInitFormData,
  getInitSheet,
} from "@shared/utils/CommonUtils";
import { ExcelUploadProps } from "@widgets/commonpopup/excel-upload";
import { showDialog } from "@widgets/CommonDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";

type S01WithModalProps = ExcelUploadProps & {
  modalRef?: React.RefObject<any>;
};

const ExcelUpload_S01_Validate = forwardRef<any, S01WithModalProps>(
  ({ modalRef, ...props }, ref) => {
    const {
      Menuprops,
      grouping,
      compareFields,
      provideDefaults,
      mappings,
      validateRow,
    } = props;

    const gridref1 = useRef<any>(null);

    const [columns1, setColums1] = useState<any[]>([]); // H 컬럼
    const [columns2, setColums2] = useState<any[]>([]); // L 컬럼
    const [sheet1Columns, setSheet1Columns] = useState<any[]>([]); // 엑셀 검증 컬럼
    const [dataSource1, setDataSource1] = useState<any>();
    const [codeDataList, setCodeDataList] = useState<Record<string, string>[]>(
      []
    );

    const ERROR_COL_ID = "__upload_error_summary";

    const [statusMessage, setStatusMessage] = useState<string>("");
    const [isValidating, setIsValidating] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    //#region grid 길이 관련
    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    const waitForGridApiReady = useCallback(async () => {
      for (let i = 0; i < 40; i += 1) {
        if (gridref1.current?.api) return true;
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      return false;
    }, []);

    const waitForRowsReady = useCallback(async (minRows: number = 1) => {
      for (let i = 0; i < 40; i += 1) {
        const cnt = gridref1.current?.api?.getDisplayedRowCount?.() ?? 0;
        if (cnt >= minRows) return true;
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      return false;
    }, []);

    const waitForColumnsReady = useCallback(async (minCols: number = 1) => {
      for (let i = 0; i < 80; i += 1) {
        const cols = gridref1.current?.api?.getColumns?.() ?? [];
        if (cols.length >= minCols) return true;
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      return false;
    }, []);

    // any 그대로 사용
    useEffect(() => {
      (async () => {
        setStatusMessage("초기화 중...");
        setIsResetting(true);
        try {
          const rtn: any = await getInitFormData();
          setColums1(rtn.grid.grid1); // H 컬럼
          setColums2(rtn.grid.grid2); // L 컬럼

          const ensuredCols = ensureErrorColumn(rtn.grid.sheet1 ?? []);
          setSheet1Columns(ensuredCols); // 엑셀 검증 컬럼

          const apiReady = await waitForGridApiReady();
          if (
            apiReady &&
            (gridref1.current?.api?.getDisplayedRowCount?.() ?? 0) === 0
          ) {
            await gridref1.current.resetGridData();
            await waitForRowsReady(1);
          }
          await waitForColumnsReady(Math.max(1, ensuredCols.length));
        } catch (error) {
        } finally {
          setIsResetting(false);
          setStatusMessage("");
        }
      })();
    }, []);

    const ensureErrorColumn = useCallback((baseCols: any[]) => {
      const cols = Array.isArray(baseCols) ? baseCols : [];
      if (cols.some((c: any) => c.cd_column === ERROR_COL_ID)) return cols;
      const errorCol = {
        cd_column: ERROR_COL_ID,
        nm_column: "오류내용",
        tp_column: "SnTextBox",
        dc_editcon: "true",
        fg_visible: "Y",
        yn_required: "N",
        qt_width: 320,
      };
      return [...cols, errorCol];
    }, []);

    // 코드치환 필요 시 주입
    useEffect(() => {
      (gridref1.current as any)?.setCodeDataList?.(codeDataList);
    }, [codeDataList]);

    /**
     * Validation 실행하여 오류 체크
     * @param updatevalue true면 검증 통과 값(코드/날짜 등)을 rowData에 반영
     * @param changedOnly true면 변경된 행만 검증(다음 진행용), false면 전체 재검증(오류체크 버튼)
     */
    const Validation = async (
      updatevalue: boolean,
      changedOnly: boolean = false
    ): Promise<boolean> => {
      setIsValidating(true);
      setStatusMessage("유효성 확인 중...");
      try {
        const api: any = gridref1.current?.api;
        if (api) {
          // SnSheet 자체 로딩 오버레이 대신 팝업의 오버레이를 쓴다.
          api.__suppressLoadingOverlay = true;
          api.__keepOverlayUntilCallerDone = true;
          api.__validateChangedOnly = changedOnly;
        }

        if (gridref1.current.api.getDisplayedRowCount() <= 1) {
          await showDialog("info", "변경된 내용이 없습니다.");
          return true;
        }

        const isValid = await gridref1.current.validateData(updatevalue);
        if (!isValid) {
          await showDialog("error", "오류내역을 확인해주세요.");
          return false;
        }
        return true;
      } finally {
        const api: any = gridref1.current?.api;
        if (api) {
          api.__suppressLoadingOverlay = false;
          api.__keepOverlayUntilCallerDone = false;
          api.__validateChangedOnly = false;
        }
        setIsValidating(false);
        setStatusMessage("");
      }
    };

    // 포커스된 행 삭제
    const onDelete = () => {
      const focusedCell = gridref1.current?.api?.getFocusedCell?.();
      if (!focusedCell) return;
      const startRowIndex = focusedCell.rowIndex ?? 0;
      gridref1.current?.deleteRowByIndex(startRowIndex);
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        setStatusMessage("초기화 중...");
        setIsResetting(true);
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
          if (gridref1.current?.api) {
            (gridref1.current.api as any).__validateChangedOnly = false;
          }
          await gridref1.current.resetGridData();
          await waitForRowsReady(1);
          // 이전 검증에서 남은 오류내용 표시 제거
          gridref1.current?.api?.forEachNode((node: any) => {
            if (!node?.rowPinned && node?.data) {
              node.setDataValue(ERROR_COL_ID, "");
            }
          });
        } finally {
          setIsResetting(false);
          setStatusMessage("");
        }
      }

      if (id == "aButton_Validation") {
        const isValid = await Validation(false, false);
        if (isValid) {
          showDialog("info", "유효성 체크가 완료되었습니다.");
        }
      }
    };

    // 2) 검증 후 다음: 통과 시 모든 업로드 행을 2단으로 전달
    const handleValidateThenNext = async () => {
      try {
        const isValid = await Validation(false, true);
        if (!isValid) {
          return;
        }

        if (gridref1.current.getChange().modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return;
        }

        await Validation(true, true);

        const validRows = collectAllRows();
        if (validRows.length === 0) {
          return;
        }

        let rows = validRows.map((r) => {
          const d1 = (provideDefaults?.(r) || {}) as any; // 초기값
          const out = { ...r, ...d1 };
          delete out[ERROR_COL_ID];

          // 공통 매핑들(무조건 세팅 + 선택 가공)
          (mappings ?? []).forEach((rule) => {
            const keyStr =
              out?.[rule.key] != null ? String(out[rule.key]).trim() : "";
            const base = rule.map[keyStr]; // ← 문자열 키로 조회

            if (base) {
              Object.assign(out, base); // 매핑값 덮어쓰기
              if (rule.transform) {
                const extra = rule.transform(out, base);
                if (extra && typeof extra === "object")
                  Object.assign(out, extra);
              }
            }
          });

          return out; //  map 콜백 내부에서 반환
        });

        // 행 단위 검증 (에러 있으면 중단)
        if (typeof validateRow === "function") {
          const errors: string[] = [];
          rows.forEach((row, i) => {
            const msg = validateRow(row);
            if (msg) errors.push(`ROW ${i + 1}: ${msg}`);
          });
          if (errors.length > 0) {
            await showDialog("error", errors.join("\n\n"));
            return;
          }
        }

        return {
          validRows: rows,
          headerColumns: columns1,
          detailColumns: columns2,
          grouping,
        };
      } catch (error: any) {}
    };

    // 상수 (빈값 체크용)
    const SKIP_FIELDS = new Set([
      "__op",
      "__state",
      "No.",
      "no",
      "rowIndex",
      "KeyField",
      ERROR_COL_ID,
    ]);

    // 1) 필드 목록: sheet1Columns 우선, 없으면 첫 행 키로 폴백 (1회 계산)
    const hasInput = (row: any) => {
      const fields = (sheet1Columns ?? [])
        .map((c: any) => c?.cd_column ?? c?.field)
        .filter((f: any) => !!f && !SKIP_FIELDS.has(f));

      const check = (v: any) =>
        v === 0 || v === false || (v != null && String(v).trim() !== "");

      if (fields.length > 0) {
        return fields.some((f) => check(row[f]));
      }

      // fallback: schema 없을 때만 시스템 키 제외
      return Object.entries(row)
        .filter(([k]) => !SKIP_FIELDS.has(k))
        .some(([, v]) => check(v));
    };

    const collectAllRows = () => {
      const api = gridref1.current?.api;
      if (!api) return [];
      const out: any[] = [];
      api.forEachNode((node: any) => {
        const r = node?.data;
        if (r && hasInput(r)) out.push({ ...r });
      });
      return out;
    };

    // grid1의 width resize
    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };
    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => {
        return await handleValidateThenNext();
      },
    }));

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={"99%"}
            minWidth={130}
            onResizeStop={() => {}}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: "99%" }}
          >
            <div style={{ position: "relative", height: "100%" }}>
              <SnSheet
                menuInfo={Menuprops}
                ref={gridref1}
                enableAddRow={true}
                columnConfig={sheet1Columns}
                rowData={dataSource1}
                isEditable={true}
                codeDataList={codeDataList}
                compareFields={compareFields ?? []}
                title={Menuprops.nm_menu}
                header={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "nowrap",
                    }}
                  >
                    <SnButton
                      text={"초기화"}
                      onClick={() => {
                        onGrid1BtnsClick("aButton_Init");
                      }}
                    />
                    <SnButton
                      text={"오류체크"}
                      onClick={() => {
                        onGrid1BtnsClick("aButton_Validation");
                      }}
                    />
                    {statusMessage && (
                      <span
                        style={{
                          marginLeft: 8,
                          color: "gray",
                          fontSize: 12,
                        }}
                      >
                        {statusMessage}
                      </span>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={onDelete}
                        sx={{
                          color: "gray",
                          "&:hover": {
                            color: "blue",
                            transform: "scale(1.2)",
                            transition: "all 0.3s ease",
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                }
              />
              {(isValidating || isResetting) && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.45)",
                    zIndex: 30,
                    pointerEvents: "none",
                    fontSize: 14,
                    color: "#1f2937",
                    fontWeight: 600,
                  }}
                >
                  {isResetting ? "초기화 중..." : "유효성 확인 중..."}
                </div>
              )}
            </div>
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);
export default ExcelUpload_S01_Validate;
